/**
 * lib/experiences/moodMeta.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Mood system for the Experiences feature.
 *
 * Exports:
 *   MoodStyle          — visual descriptor for one mood
 *   MOOD_META          — record of all 10 moods
 *   deriveDayMood(day) — compute mood key from an ExperienceDay
 *
 * Used by:
 *   routes/experiences.tsx — DayCard mood header + card tinting
 */

import type { ExperienceDay } from "@/lib/types";

// ─── Mood style type ──────────────────────────────────────────────────────────

export interface MoodStyle {
  icon:        string;
  label:       string;
  descriptor:  string;
  /** oklch color string — primary accent */
  accent:      string;
  /** accent at low opacity — for subtle bg tinting */
  accentDim:   string;
  /** card background gradient */
  cardBg:      string;
  /** mood header strip background */
  stripBg:     string;
  /** text color on the strip */
  stripText:   string;
}

// ─── Mood registry ────────────────────────────────────────────────────────────

export const MOOD_META: Readonly<Record<string, MoodStyle>> = {
  foodie: {
    icon:       "🍜",
    label:      "Sabores locales",
    descriptor: "Mercados, sabores y rincones locales",
    accent:     "oklch(0.68 0.11 55)",
    accentDim:  "oklch(0.68 0.11 55 / 0.12)",
    cardBg:     "linear-gradient(160deg, oklch(0.993 0.010 70) 0%, oklch(0.987 0.018 62) 100%)",
    stripBg:    "linear-gradient(90deg, oklch(0.985 0.028 78 / 0.95) 0%, oklch(0.982 0.018 65 / 0.85) 100%)",
    stripText:  "oklch(0.50 0.10 55)",
  },
  nightlife: {
    icon:       "🌃",
    label:      "Noche local",
    descriptor: "La ciudad cuando baja el sol",
    accent:     "oklch(0.50 0.14 262)",
    accentDim:  "oklch(0.50 0.14 262 / 0.10)",
    cardBg:     "linear-gradient(160deg, oklch(0.990 0.008 250) 0%, oklch(0.982 0.016 262) 100%)",
    stripBg:    "linear-gradient(90deg, oklch(0.970 0.020 268 / 0.95) 0%, oklch(0.965 0.016 255 / 0.85) 100%)",
    stripText:  "oklch(0.40 0.13 262)",
  },
  culture: {
    icon:       "🏛️",
    label:      "Cultura local",
    descriptor: "Historia, patrimonio y calles abiertas",
    accent:     "oklch(0.45 0.09 232)",
    accentDim:  "oklch(0.45 0.09 232 / 0.10)",
    cardBg:     "linear-gradient(160deg, oklch(0.990 0.008 232) 0%, oklch(0.982 0.014 225) 100%)",
    stripBg:    "linear-gradient(90deg, oklch(0.972 0.016 235 / 0.95) 0%, oklch(0.966 0.012 225 / 0.85) 100%)",
    stripText:  "oklch(0.35 0.09 232)",
  },
  creative: {
    icon:       "🎨",
    label:      "Arte y creatividad",
    descriptor: "Arte, expresión y espacios de diseño",
    accent:     "oklch(0.58 0.16 342)",
    accentDim:  "oklch(0.58 0.16 342 / 0.10)",
    cardBg:     "linear-gradient(160deg, oklch(0.990 0.010 342) 0%, oklch(0.982 0.016 328) 100%)",
    stripBg:    "linear-gradient(90deg, oklch(0.975 0.022 345 / 0.95) 0%, oklch(0.968 0.018 330 / 0.85) 100%)",
    stripText:  "oklch(0.42 0.14 342)",
  },
  nature: {
    icon:       "🌿",
    label:      "Al aire libre",
    descriptor: "Verde, parques y perspectiva abierta",
    accent:     "oklch(0.48 0.13 148)",
    accentDim:  "oklch(0.48 0.13 148 / 0.10)",
    cardBg:     "linear-gradient(160deg, oklch(0.990 0.010 148) 0%, oklch(0.982 0.016 138) 100%)",
    stripBg:    "linear-gradient(90deg, oklch(0.974 0.022 152 / 0.95) 0%, oklch(0.968 0.016 140 / 0.85) 100%)",
    stripText:  "oklch(0.36 0.12 148)",
  },
  slow: {
    icon:       "✨",
    label:      "Ritmo suave",
    descriptor: "Ritmo suave y barrios tranquilos",
    accent:     "oklch(0.58 0.09 158)",
    accentDim:  "oklch(0.58 0.09 158 / 0.10)",
    cardBg:     "linear-gradient(160deg, oklch(0.992 0.008 158) 0%, oklch(0.986 0.012 148) 100%)",
    stripBg:    "linear-gradient(90deg, oklch(0.978 0.016 162 / 0.95) 0%, oklch(0.972 0.012 150 / 0.85) 100%)",
    stripText:  "oklch(0.40 0.09 158)",
  },
  anime: {
    icon:       "⛩️",
    label:      "Cultura popular",
    descriptor: "Anime, manga y cultura de vanguardia",
    accent:     "oklch(0.55 0.15 286)",
    accentDim:  "oklch(0.55 0.15 286 / 0.10)",
    cardBg:     "linear-gradient(160deg, oklch(0.988 0.012 290) 0%, oklch(0.980 0.020 278) 100%)",
    stripBg:    "linear-gradient(90deg, oklch(0.972 0.022 292 / 0.95) 0%, oklch(0.965 0.018 280 / 0.85) 100%)",
    stripText:  "oklch(0.40 0.13 286)",
  },
  music: {
    icon:       "🎵",
    label:      "Música en vivo",
    descriptor: "Música en vivo y noches con ritmo propio",
    accent:     "oklch(0.58 0.13 32)",
    accentDim:  "oklch(0.58 0.13 32 / 0.10)",
    cardBg:     "linear-gradient(160deg, oklch(0.991 0.012 36) 0%, oklch(0.984 0.018 26) 100%)",
    stripBg:    "linear-gradient(90deg, oklch(0.978 0.022 38 / 0.95) 0%, oklch(0.972 0.018 28 / 0.85) 100%)",
    stripText:  "oklch(0.42 0.12 32)",
  },
  architecture: {
    icon:       "🏗️",
    label:      "Arquitectura urbana",
    descriptor: "Forma, espacio y diseño urbano",
    accent:     "oklch(0.48 0.08 212)",
    accentDim:  "oklch(0.48 0.08 212 / 0.10)",
    cardBg:     "linear-gradient(160deg, oklch(0.990 0.008 215) 0%, oklch(0.983 0.012 205) 100%)",
    stripBg:    "linear-gradient(90deg, oklch(0.974 0.016 218 / 0.95) 0%, oklch(0.968 0.012 206 / 0.85) 100%)",
    stripText:  "oklch(0.35 0.08 212)",
  },
  default: {
    icon:       "🌍",
    label:      "Exploración",
    descriptor: "Un día de exploración libre",
    accent:     "oklch(0.60 0.08 200)",
    accentDim:  "oklch(0.60 0.08 200 / 0.10)",
    cardBg:     "linear-gradient(160deg, oklch(0.992 0.006 200) 0%, oklch(0.986 0.010 190) 100%)",
    stripBg:    "linear-gradient(90deg, oklch(0.976 0.012 202 / 0.95) 0%, oklch(0.970 0.010 192 / 0.85) 100%)",
    stripText:  "oklch(0.42 0.08 200)",
  },
};

// ─── Category → mood key mapping ─────────────────────────────────────────────

const CATEGORY_TO_MOOD: Readonly<Record<string, string>> = {
  food:         "foodie",
  nightlife:    "nightlife",
  culture:      "culture",
  art:          "creative",
  nature:       "nature",
  wellness:     "slow",
  anime:        "anime",
  music:        "music",
  architecture: "architecture",
};

// ─── Mood derivation ──────────────────────────────────────────────────────────

/**
 * Derive the mood key for a day from its experiences.
 *
 * Priority:
 *   1. Energy-based overrides (high + nightlife → nightlife; low + wellness/nature → slow)
 *   2. Dominant category by count
 *   3. Falls back to "default"
 */
export function deriveDayMood(day: ExperienceDay): string {
  if (!day.experiences.length) return "default";

  const cats = day.experiences.map(e => e.category);
  const counts: Record<string, number> = {};
  for (const c of cats) counts[c] = (counts[c] ?? 0) + 1;

  // Energy-based overrides
  if (day.energy === "high" && cats.includes("nightlife")) return "nightlife";
  if (day.energy === "low"  && (cats.includes("wellness") || cats.includes("nature"))) return "slow";

  // Dominant category
  let dominant = "culture";
  let max = 0;
  for (const [cat, count] of Object.entries(counts)) {
    if (count > max) { max = count; dominant = cat; }
  }

  return CATEGORY_TO_MOOD[dominant] ?? "default";
}
